import React, { useState } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Form } from "react-bootstrap";
import { Snackbar, Alert, CircularProgress } from "@mui/material";
import { Input } from "Components/Input/Input";
import { Button } from "Components/Button/Button";
import { existsUser } from "Services/usuario_service";
import { getInfoRenap } from "Services/renap_service";
import { useDispatch } from "react-redux";
import { RegisterUser } from "../../Services/login_service";
import { setAuthUser } from "../../Slices/Login/loginSlice";
import { setActiveDrawer } from "../../Slices/uiSlice";
import { storageService } from "../../Services/storage_service";
import { PrivacyPolicyModal } from "Components/PrivacyPolicyModal/PrivacyPolicyModal";
import { TermsAndConditionsModal } from "Components/TermsAndConditionsModal/TermsAndConditionsModal";
import { paramService } from "Services/param_service";
import { useQuery } from "Hooks/useQuery";
import { useNotificationTimer } from "Hooks/useNotificationTimer";
import { useSyncronization } from "Hooks/useSyncronization";
import { useCursosPaquetes } from "Hooks/useCursosPaquetes";

const step1Schema = Yup.object({
  dpi: Yup.string()
    .required("El DPI es obligatorio")
    .matches(/^\d{13}$/, "El DPI debe tener exactamente 13 dígitos"),
});

const step2Schema = Yup.object({
  dpi: Yup.string()
    .required("El DPI es obligatorio")
    .matches(/^\d{13}$/, "El DPI debe tener exactamente 13 dígitos"),
  nombres: Yup.string()
    .trim()
    .required("Los nombres son obligatorios")
    .matches(
      /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\u1D2E0-\u1D2F3\s]+$/u,
      "Los nombres solo pueden contener letras y espacios"
    ),
  apellidos: Yup.string()
    .trim()
    .required("Los apellidos son obligatorios")
    .matches(
      /^[a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\u1D2E0-\u1D2F3\s]+$/u,
      "Los apellidos solo pueden contener letras y espacios"
    ),
  fechaNacimiento: Yup.string()
    .trim()
    .required("La fecha de nacimiento es obligatoria"),
  telefono: Yup.string()
    .matches(/^\d{8}$/, "El teléfono debe tener 8 dígitos")
    .optional(),
  foto: Yup.string().optional().nullable(),
});

export const RegisterForm = ({ currentStep, setCurrentStep, isMobile }) => {
  const dispatch = useDispatch();
  const { startNotificationTimer } = useNotificationTimer();
  const { syncProgress, checkForSyncronization } = useSyncronization();
  const { refetch: refetchCursosPaquetes } = useCursosPaquetes();
  const activarRenap = Boolean(storageService.getItem("activarRenap"));
  const [openPrivacyPolicy, setOpenPrivacyPolicy] = useState(false);
  const [openTermsAndConditions, setOpenTermsAndConditions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });

  const { data: legal } = useQuery(paramService.param, {
    grupo: "legal",
  });

  const getParamValue = (codigo) => {
    if (!legal?.data || !Array.isArray(legal?.data)) return null;
    const param = legal?.data.find((p) => p.codigoParametro === codigo);
    return param?.parametro || null;
  };

  const terminos = getParamValue("terminos") || "";
  const politicas = getParamValue("politicas") || "";

  const formik = useFormik({
    initialValues: {
      dpi: "",
      nombres: "",
      apellidos: "",
      fechaNacimiento: "",
      telefono: "",
      foto: "",
    },
    validationSchema: currentStep === 1 ? step1Schema : step2Schema,
    enableReinitialize: true,
    validateOnMount: false,
    onSubmit: async (values) => {
      if (currentStep === 1) {
        await handleDpiValidation(values.dpi);
      } else {
        await handleCreateAccount(values);
      }
    },
  });

  const handleDpiValidation = async (dpi) => {
    setLoading(true);
    try {
      const userValidation = await existsUser({ user: dpi });
      const canCreateUser = Boolean(userValidation.result);

      if (!canCreateUser) {
        const errorMessage = userValidation?.message || "El usuario ya existe";
        showSnackbar(errorMessage, "error");
        return;
      }

      if (activarRenap) {
        const infoRenap = await getInfoRenap({ DPI: dpi });
        const validDpi = Boolean(infoRenap.result);

        if (!validDpi) {
          const errorMessage =
            infoRenap?.message || "Error al validar con RENAP";
          showSnackbar(errorMessage, "error");
          return;
        }

        const nombres = [
          infoRenap.data.PRIMER_NOMBRE,
          infoRenap.data.SEGUNDO_NOMBRE,
          infoRenap.data.TERCER_NOMBRE,
        ]
          .filter((nombre) => nombre && nombre.trim() !== "")
          .join(" ");

        const apellidos = [
          infoRenap.data.PRIMER_APELLIDO,
          infoRenap.data.SEGUNDO_APELLIDO,
        ]
          .filter((apellido) => apellido && apellido.trim() !== "")
          .join(" ");

        const fechaRenap = infoRenap.data.FECHA_NACIMIENTO;
        const [dia, mes, año] = fechaRenap.split("/");
        const fechaParaInput = `${año}-${mes.padStart(2, "0")}-${dia.padStart(
          2,
          "0"
        )}`;

        formik.setValues({
          ...formik.values,
          nombres,
          apellidos,
          fechaNacimiento: fechaParaInput,
          foto: infoRenap.data.FOTO,
        });
        setCurrentStep(2);
      } else {
        setCurrentStep(2);
      }
    } catch (error) {
      showSnackbar("Error al validar el DPI. Intenta nuevamente.", "error");
    } finally {
      formik.setTouched({}, false);
      setLoading(false);
    }
  };

  const handleCreateAccount = async (values) => {
    setLoading(true);
    try {
      const registrationData = {
        user: values.dpi,
        dpi: values.dpi,
        nombres: values.nombres,
        apellidos: values.apellidos,
        fechaNacimiento: values.fechaNacimiento,
        telefono: values.telefono,
        foto: values.foto,
      };
      const respUser = await RegisterUser(registrationData);

      if (respUser.result === 1 && respUser.data && respUser.data.length > 0) {
        dispatch(setAuthUser(respUser.data[0]));
        storageService.setItem("user", respUser.data[0]);

        const hasSync = checkForSyncronization();

        if (hasSync) {
          showSnackbar("Sincronizando progreso...", "info");
          await syncProgress(respUser.data[0]);
        }

        showSnackbar("Cuenta creada exitosamente", "success");

        setTimeout(() => {
          dispatch(setActiveDrawer(null));
          startNotificationTimer();
          refetchCursosPaquetes();
        }, 2000);
      } else {
        const errorMessage = respUser.message || "Error al crear la cuenta";
        showSnackbar(errorMessage, "error");
      }
    } catch (error) {
      showSnackbar("Error al crear la cuenta", "error");
    } finally {
      setLoading(false);
    }
  };

  const showSnackbar = (message, severity) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const handleOpenPrivacyPolicy = () => {
    setOpenPrivacyPolicy(true);
  };

  const handleClosePrivacyPolicy = () => {
    setOpenPrivacyPolicy(false);
  };

  const handleOpenTermsAndConditions = () => {
    setOpenTermsAndConditions(true);
  };

  const handleCloseTermsAndConditions = () => {
    setOpenTermsAndConditions(false);
  };

  const handleDpiChange = (e) => {
    const numbersOnly = e.target.value.replace(/\D/g, "").slice(0, 13);
    formik.setFieldValue("dpi", numbersOnly);
  };

  const handleDpiBlur = (e) => {
    if (formik.values.dpi.length > 0) {
      formik.handleBlur(e);
    }
  };

  const handleTelefonoChange = (e) => {
    const numbersOnly = e.target.value.replace(/\D/g, "").slice(0, 8);
    formik.setFieldValue("telefono", numbersOnly);
  };

  const handleNombresChange = (e) => {
    const lettersOnly = e.target.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]/g, "");
    formik.setFieldValue("nombres", lettersOnly);
  };

  const handleApellidosChange = (e) => {
    const lettersOnly = e.target.value.replace(/[^a-zA-ZáéíóúÁÉÍÓÚñÑüÜ\s]/g, "");
    formik.setFieldValue("apellidos", lettersOnly);
  };

  return (
    <>
      <Form
        onSubmit={formik.handleSubmit}
        noValidate
        className="register-drawer__form"
      >
        {currentStep === 1 && (
          <Input
            label="DPI"
            name="dpi"
            id="dpi"
            type="number"
            placeholder="0000 00000 0000"
            value={formik.values.dpi}
            onChange={handleDpiChange}
            onBlur={handleDpiBlur}
            isInvalid={formik.touched.dpi && !!formik.errors.dpi}
            errorMessage={formik.errors.dpi}
            disabled={loading}
            maxLength={13}
            autoComplete="off"
            autoFocus
          />
        )}

        {currentStep === 2 && (
          <>
            <Input
              label="DPI"
              name="dpi"
              id="dpi"
              type="text"
              value={formik.values.dpi}
              isValid={true}
              readOnly
            />

            <Input
              label="Nombres"
              name="nombres"
              id="nombres"
              type="text"
              value={formik.values.nombres}
              onChange={handleNombresChange}
              onBlur={formik.handleBlur}
              isValid={
                formik.touched.nombres && formik.values.nombres && !formik.errors.nombres
              }
              isInvalid={
                formik.touched.nombres && !!formik.errors.nombres
              }
              errorMessage={formik.errors.nombres}
              disabled={loading || activarRenap}
              placeholder="Nombres"
              autoFocus
            />

            <Input
              label="Apellidos"
              name="apellidos"
              id="apellidos"
              type="text"
              value={formik.values.apellidos}
              onChange={handleApellidosChange}
              onBlur={formik.handleBlur}
              isValid={
                formik.touched.apellidos && formik.values.apellidos && !formik.errors.apellidos
              }
              isInvalid={
                formik.touched.apellidos && !!formik.errors.apellidos
              }
              errorMessage={formik.errors.apellidos}
              disabled={loading || activarRenap}
              placeholder="Apellidos"
            />

            <Input
              label="Fecha de nacimiento"
              name="fechaNacimiento"
              id="fechaNacimiento"
              type="date"
              max={new Date().toISOString().split("T")[0]}
              value={formik.values.fechaNacimiento}
              onChange={formik.handleChange}
              onBlur={formik.handleBlur}
              isValid={
                formik.values.fechaNacimiento && !formik.errors.fechaNacimiento
              }
              isInvalid={
                formik.touched.fechaNacimiento &&
                !!formik.errors.fechaNacimiento
              }
              errorMessage={formik.errors.fechaNacimiento}
              disabled={loading || activarRenap}
              placeholder="DD/MM/AAAA"
            />

            <Input
              label="Número de teléfono"
              name="telefono"
              id="telefono"
              type="text"
              inputMode="numeric"
              placeholder="5544 3322"
              value={formik.values.telefono}
              onChange={handleTelefonoChange}
              onBlur={formik.handleBlur}
              isInvalid={formik.touched.telefono && !!formik.errors.telefono}
              errorMessage={formik.errors.telefono}
              disabled={loading}
              maxLength={8}
            />
          </>
        )}

        {currentStep === 2 && (
          <div className="register-drawer__terms">
            <p className="register-drawer__terms-text">
              Al ingresar aceptarás nuestros
              <br />
              <span
                className="register-drawer__terms-link"
                onClick={handleOpenTermsAndConditions}
              >
                Términos de uso
              </span>{" "}
              y{" "}
              <span
                className="register-drawer__terms-link"
                onClick={handleOpenPrivacyPolicy}
              >
                Políticas de privacidad
              </span>
            </p>
          </div>
        )}

        <Button
          type="submit"
          disabled={
            loading ||
            (currentStep === 1 && (!formik.values.dpi || !!formik.errors.dpi))
          }
          className={currentStep === 2 && isMobile && "mb-4"}
        >
          {loading && <CircularProgress size={20} sx={{ marginRight: 1 }} />}

          {loading
            ? currentStep === 1
              ? "VALIDANDO..."
              : "CREANDO CUENTA..."
            : currentStep === 1
            ? "SIGUIENTE"
            : "CREAR CUENTA"}
        </Button>
      </Form>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <TermsAndConditionsModal
        openTermsAndConditions={openTermsAndConditions}
        handleCloseTermsAndConditions={handleCloseTermsAndConditions}
        terminos={terminos}
      />

      <PrivacyPolicyModal
        openPrivacyPolicy={openPrivacyPolicy}
        handleClosePrivacyPolicy={handleClosePrivacyPolicy}
        politicas={politicas}
      />
    </>
  );
};
